// Shared catalog used by both Design Studio (/design-studio) and
// the FabricSelection consultation modal (BespokeJourney).
// Source of truth — edit here to sync both surfaces.

export const fabricOptions = [
  {
    id: 1,
    name: "Pure Silk",
    description:
      "The eternal heart of our atelier. Handwoven mulberry silk with a luminous sheen and graceful drape — the foundation of every heirloom saree and lehenga.",
    origin: "Varanasi & Mysore",
    availability: "In Stock",
    leadTime: "2–3 weeks",
    priceRange: "Premium",
    image:
      "https://images.pexels.com/photos/8465948/pexels-photo-8465948.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 2,
    name: "Uppada Silk",
    description:
      "A handloom marvel from coastal Andhra. Fine jamdani weaving produces near-weightless silk with motifs woven directly into the warp — soft, fluid, dignified.",
    origin: "Uppada, Andhra Pradesh",
    availability: "In Stock",
    leadTime: "3–4 weeks",
    priceRange: "Premium",
    image:
      "https://images.pexels.com/photos/10317113/pexels-photo-10317113.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 3,
    name: "Raw Silk",
    description:
      "Unrefined, textured, and quietly powerful. The natural slubs of raw silk give structure to anarkalis and sharara sets while preserving the soul of the cocoon.",
    origin: "Bhagalpur",
    availability: "In Stock",
    leadTime: "2–3 weeks",
    priceRange: "Classic",
    image:
      "https://images.pexels.com/photos/35993328/pexels-photo-35993328.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 4,
    name: "Semi-Silk",
    description:
      "A considered blend of silk and fine cotton — the drape of silk with the breathability of cotton. Ideal for day ceremonies and trans-seasonal couture.",
    origin: "Kanchipuram",
    availability: "In Stock",
    leadTime: "2–3 weeks",
    priceRange: "Classic",
    image:
      "https://images.pexels.com/photos/7717488/pexels-photo-7717488.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 5,
    name: "Crepe",
    description:
      "A whisper-soft crinkled weave that falls in liquid lines. Crepe flatters every silhouette — the quiet choice for modern couture and cocktail saris.",
    origin: "French & Indian mills",
    availability: "In Stock",
    leadTime: "2–3 weeks",
    priceRange: "Classic",
    image:
      "https://images.pexels.com/photos/8465945/pexels-photo-8465945.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 6,
    name: "Organza",
    description:
      "Sheer, crisp, and ethereal. A fabric that holds light. Perfect for dupattas, layered lehengas, and statement sleeves that float rather than fall.",
    origin: "Bangalore",
    availability: "In Stock",
    leadTime: "2–3 weeks",
    priceRange: "Premium",
    image:
      "https://images.unsplash.com/photo-1650091902768-49da57745e3f?crop=entropy&cs=srgb&fm=jpg&w=940&q=85",
  },
  {
    id: 7,
    name: "Satin",
    description:
      "A glossy, sensuous finish with a mirror-like sheen. Used for bodices, linings, and contemporary slip-cut silhouettes that demand movement.",
    origin: "Italian & Indian mills",
    availability: "In Stock",
    leadTime: "2–3 weeks",
    priceRange: "Premium",
    image:
      "https://images.pexels.com/photos/8767580/pexels-photo-8767580.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 8,
    name: "Tissue",
    description:
      "Metallic-threaded silk with a molten, liquid-gold shimmer. Reserved for grand occasions — sangeet, reception, or the moment you wish to be remembered.",
    origin: "Chanderi",
    availability: "Limited Stock",
    leadTime: "3–4 weeks",
    priceRange: "Luxury",
    image:
      "https://images.pexels.com/photos/35977504/pexels-photo-35977504.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 9,
    name: "Velvet",
    description:
      "Deep, plush, and regal. Silk velvet in jewel tones — the choice for winter weddings and heirloom anarkalis. A fabric that remembers every light it touches.",
    origin: "Italian Silk Velvet",
    availability: "Made to Order",
    leadTime: "4–5 weeks",
    priceRange: "Ultra Luxury",
    image:
      "https://images.pexels.com/photos/6843282/pexels-photo-6843282.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
];

export const embroideryOptions = [
  {
    id: 1,
    name: "Aari / Zardozi Work",
    description:
      "The twin crown jewels of Indian embroidery. Aari uses a fine hook to trace flowing chain-stitch motifs, while Zardozi layers real gold and silver threads into raised, royal relief — a craft patronised by Mughal emperors.",
    hours: "200–400 hours",
    availability: "Available",
    craftsmen: "Master Karigars, Lucknow",
    image:
      "https://images.pexels.com/photos/8886950/pexels-photo-8886950.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 2,
    name: "Dabka",
    description:
      "Coiled metallic wire stitched flat to the fabric, producing a matte-lustrous relief. Dabka catches light at every angle, giving grooms' sherwanis and bridal cholis their signature depth.",
    hours: "150–300 hours",
    availability: "Available",
    craftsmen: "Specialist Artisans",
    image:
      "https://images.pexels.com/photos/2933639/pexels-photo-2933639.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 3,
    name: "Naqshi",
    description:
      "Raised embossed embroidery built up with cotton padding and overlaid in metallic or silk thread. Naqshi creates the three-dimensional motifs seen on heritage dupattas and bridal borders.",
    hours: "180–350 hours",
    availability: "Available",
    craftsmen: "Heritage Artisans",
    image:
      "https://images.pexels.com/photos/8751904/pexels-photo-8751904.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 4,
    name: "Kamdani",
    description:
      "A delicate Lucknowi technique where flattened silver or gold thread is stitched in fine dots across sheer fabrics — luminous, weightless, almost starlight across the weave.",
    hours: "120–220 hours",
    availability: "Available",
    craftsmen: "Lucknowi Karigars",
    image:
      "https://images.pexels.com/photos/28831611/pexels-photo-28831611.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
  {
    id: 5,
    name: "Fine Thread Embroidery",
    description:
      "Silk resham threadwork in soft tonal or multicoloured palettes — paisleys, florals, and contemporary abstractions. The most versatile of our techniques, worn from day to dusk.",
    hours: "100–200 hours",
    availability: "Available",
    craftsmen: "Traditional Artisans",
    image:
      "https://images.pexels.com/photos/35623927/pexels-photo-35623927.jpeg?auto=compress&cs=tinysrgb&w=940",
  },
];
